import {
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Link,
  Paper,
  Typography,
} from '@material-ui/core';
import { unstable_Box as Box } from '@material-ui/core/Box';
import { withStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import MailIcon from '@material-ui/icons/MailOutline';
import { mdiCardAccountDetailsOutline, mdiCellphoneNfc } from '@mdi/js';
import Icon from '@mdi/react';
import config from '_config';
import moment from 'moment';
import React, { useState } from 'react';
import { useEffect } from 'react';
import { connect } from 'react-redux';

import { Primaccess } from '@aps-management/primapp-common';

import useLoadingState from '_hooks/useLoadingState';
import masterClient from '_utils/masterClient';

/* */
const styles = (theme) => ({
  badgeIcon: {
    color: theme.palette.secondary.main,
  },
  addToWalletImg: {
    height: 48,
    minHeight: 48,
  },
  leftIcon: {
    marginRight: theme.spacing.unit,
  },
  successButton: {
    color: '#27AE60',
    '&$disabled': {
      color: '#27AE60',
    },
  },
  disabled: {},
});

const IMAGE_URL = 'https://storage.googleapis.com/primaccess-wallet-pass';

/* */
const BadgesList = ({ golf, account, classes, ...props }) => {
  const { client, permissions, onError } = props;

  const { badges = [], nfcPasses: initNfcPasses = [] } = client;

  const [isMailSent, setIsMailSent] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [nfcPasses, setNfcPasses] = useState(initNfcPasses);
  const { isLoading, setLoading } = useLoadingState();

  useEffect(() => {
    if (!isMailSent) return;
    const timer = setTimeout(() => {
      setIsMailSent(false);
    }, 2000);

    return () => {
      clearTimeout(timer);
    };
  }, [isMailSent]);

  /* */
  const handleAssignNfcPass = () => {
    onError(null);
    setLoading('assignNfcPass', true);

    Primaccess.api
      .assignNfcPass(masterClient, {
        golfId: golf.id,
        clientId: client.id,
        accountId: account.id,
      })
      .then((pass) => {
        setNfcPasses((prev) => [pass, ...prev]);
        setIsModalOpen(true);
      })
      .catch((err) => onError(err.message))
      .finally(() => setLoading('assignNfcPass', false));
  };

  /* */
  const handleSendNfcPass = (nfcPassId) => {
    onError(null);
    setLoading('sendNfcPass', true);

    Primaccess.api
      .sendNfcPass(masterClient, {
        golfId: golf.id,
        badgeId: nfcPassId,
        clientId: client.id,
        accountId: account.id,
      })
      .then(() => setIsMailSent(true))
      .catch((err) => onError(err.message))
      .finally(() => setLoading('sendNfcPass', false));
  };

  /* */
  const renderSendNfcPassButton = (nfcPass) => {
    if (isMailSent) {
      return (
        <Button
          disabled
          fullWidth
          size='small'
          classes={{
            root: classes.successButton,
            disabled: classes.disabled,
          }}
        >
          <CheckCircleIcon className={classes.leftIcon} /> {'Demande effectuée'}
        </Button>
      );
    }

    return (
      <Button
        fullWidth
        size='small'
        variant='outlined'
        color='secondary'
        disabled={isLoading('sendNfcPass')}
        onClick={() => handleSendNfcPass(nfcPass.id)}
      >
        {isLoading('sendNfcPass') ? (
          <CircularProgress size={16} className={classes.leftIcon} />
        ) : (
          <MailIcon className={classes.leftIcon} />
        )}
        {'Renvoyer par e-mail'}
      </Button>
    );
  };

  /* */
  const renderNfcPassDownloadLink = (nfcPass) => {
    if (!nfcPass.isAssigned) return null;

    let altImg;
    let platform;
    if (/iPhone/i.test(navigator.userAgent)) {
      altImg = "Ajouter à l'app Cartes Apple";
      platform = 'apple'; // pass/{p.token}/apple
    } else if (/Android/i.test(navigator.userAgent)) {
      altImg = 'Ajouter au Google Wallet';
      platform = 'google'; // pass/{p.token}/google
    }

    return (
      <Box marginTop={1} display='flex' justifyContent='center'>
        {platform ? (
          <ButtonBase
            rel='noopener'
            target='_blank'
            component={Link}
            href={`${config.primaccessPassUrl}/pass/${nfcPass.token}/${platform}`}
          >
            <img alt={altImg} className={classes.addToWalletImg} src={`${IMAGE_URL}/${platform}/add-to-wallet.svg`} />
          </ButtonBase>
        ) : (
          renderSendNfcPassButton(nfcPass)
        )}
      </Box>
    );
  };

  /*  */
  const renderBadgeOrPass = (p) => {
    const isNfc = Object.hasOwn(p, 'token');

    const status = !p.isAssigned ? 'Désactivée' : p.isEnabled ? 'Activée' : "En cours d'activation";

    const statusColor = !p.isAssigned ? '#C0392B' : p.isEnabled ? '#27AE60' : '#E67E22';

    const label = isNfc ? 'Carte virtuelle' : 'Badge';
    const icon = isNfc ? mdiCellphoneNfc : mdiCardAccountDetailsOutline;

    return (
      <Box key={p.id} padding={2} borderBottom={1} borderColor='#dfdfdf'>
        <Box display='flex' flexDirection='row' alignItems='center'>
          <Icon path={icon} size={1.75} className={classes.badgeIcon} />
          <Box marginLeft={2}>
            <Typography variant='body1' color='primary'>
              {label} <b>{`#${p.id}`}</b>
            </Typography>
            {isNfc && (
              <Typography variant='body2' color='inherit' style={{ color: statusColor }}>
                {status}
              </Typography>
            )}
            <Typography variant='caption' color='secondary'>
              {`Attribué le ${moment(p.startDate).format('L')}`}
            </Typography>
          </Box>
        </Box>
        {isNfc && renderNfcPassDownloadLink(p)}
      </Box>
    );
  };

  /* */
  const renderNfcPassModal = () => {
    return (
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <DialogContent>
          <DialogContentText variant='body2' paragraph>
            Votre carte est en cours d'activation.
            <br />
            Un e-mail vous sera envoyé à <b>{account.email}</b> afin de l’ajouter à l'application <u>Cartes Apple</u> ou{' '}
            <u>Google Wallet</u>.
          </DialogContentText>
          <DialogContentText align='center'>
            <img height='60' alt='Cartes Apple et Google Wallet' src={`${IMAGE_URL}/logos_wallet.png`} />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsModalOpen(false)} color='primary'>
            {'OK'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <>
      <br />
      <Typography gutterBottom variant='h6' component='h2'>
        {'Mes badges'}
      </Typography>
      {nfcPasses.length === 0 && permissions.nfcPass && (
        <Button
          fullWidth
          color='secondary'
          variant='contained'
          disabled={isLoading('assignNfcPass')}
          style={{ marginBottom: 12 }}
          onClick={handleAssignNfcPass}
        >
          {isLoading('assignNfcPass') ? (
            <CircularProgress size={16} className={classes.leftIcon} />
          ) : (
            'Activer ma carte virtuelle'
          )}
        </Button>
      )}
      {nfcPasses.length + badges.length > 0 ? (
        <Paper>
          {nfcPasses.map(renderBadgeOrPass)}
          {badges.map(renderBadgeOrPass)}
        </Paper>
      ) : (
        <Typography>{'Aucun badge attribué.'}</Typography>
      )}
      <br />
      {client.isSU && (
        <Typography gutterBottom component='span' variant='h6'>
          {'Super Utilisateur'}
        </Typography>
      )}
      {client.expiredAt && (
        <Typography gutterBottom component='span' variant='h6'>
          {`Expire le ${moment(client.expiredAt).format('L')}`}
        </Typography>
      )}
      {renderNfcPassModal()}
    </>
  );
};

const mapStateToProps = ({ app: { golf, account } }) => ({ golf, account });

const StyledComponent = withStyles(styles)(BadgesList);

export default connect(mapStateToProps, Primaccess.actions)(StyledComponent);
